import { useCallback, useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAuth } from "@/contexts/AuthContext";
import {
  useProjects,
  validateSelectedProjects,
} from "@/contexts/ProjectContext";
import { PROJECT_SELECTION_CHANGED_EVENT } from "@/components/ProjectSwitcher";
import { llmApi } from "@/services/rag-service";
import {
  getKeycloakUserInfo,
  isKeycloakAuthenticated,
} from "@/services/keycloak";
import { isUserAdmin } from "@/utils/auth-helpers";
import CatalogPagination from "./components/CatalogPagination";
import CatalogTable from "./components/CatalogTable";
import CatalogToolbar from "./components/CatalogToolbar";
import { getSourceOptions, getTypeOptions } from "./constants/modelFields";
import AICatalogConfirmDeleteComp from "./ConfirmDelete";
import AICatalogDetailComp from "./Detail";
import AICatalogFormComp from "./Form";
import AICatalogShareComp from "./Share";
import useCopyToClipboard from "./hooks/useCopyToClipboard";
import type {
  CatalogOrder,
  LLMConfigsResponse,
  LLMModel,
  ModelFilterType,
  ModelSourceFilter,
  Provider,
} from "./types";
import {
  defaultProvider,
  getApiErrorMessage,
  sortModelsByName,
} from "./utils/catalog";

const AICatalogComp = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const { cache, currentUser, userProfile, authProvider } = useAuth();
  const { selectedProjects } = useProjects();

  const [detailOpen, setDetailOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedValue, setDebouncedValue] = useState("");
  const [models, setModels] = useState<LLMModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [provider, setProvider] = useState("");
  const [source, setSource] = useState<ModelSourceFilter>("");
  const [type, setType] = useState<ModelFilterType>("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pageCount, setPageCount] = useState(0);
  const [order, setOrder] = useState<CatalogOrder>("asc");
  const [isAdmin, setIsAdmin] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState<LLMModel | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [, setCurrentUserEmail] = useState("");
  const copyToClipboard = useCopyToClipboard();

  const openMenu = Boolean(anchorEl);

  const sources = useMemo(() => getSourceOptions(t), [t]);
  const types = useMemo(() => getTypeOptions(t), [t]);
  const safeSelectedProjects = useMemo(
    () => validateSelectedProjects(selectedProjects) as string[],
    [selectedProjects],
  );
  const [providers, setProviders] = useState<Provider[]>([]);

  const fetchProviders = useCallback(async () => {
    setLoading(true);
    try {
      const response = (await llmApi.getProviders()) as Provider[];
      setProviders(response || []);
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, t("modelRegistration.failedToLoadProviders")),
      );
      setProviders([defaultProvider]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchModels = useCallback(async () => {
    setLoading(true);
    setModels([]);
    setPageCount(0);
    try {
      const options: Record<string, unknown> = {
        page: Number(page) - 1,
        limit: pageSize,
      };

      if (debouncedValue.length) options.name = debouncedValue;
      if (safeSelectedProjects && !safeSelectedProjects.includes("all")) {
        options.project_ids = safeSelectedProjects;
      }
      if (type) options.type = type;
      if (provider) options.provider = provider;

      if (source === "shared") {
        options.include_shared = true;
      } else if (source === "bunyan") {
        options.include_public = true;
      } else if (source === "private") {
        options.include_public = false;
      }

      const response = (await llmApi.getLLMConfigs(
        options as any,
      )) as unknown as LLMConfigsResponse;
      setModels(response.items || []);
      setPageCount(Math.ceil((response?.total_count || 0) / (pageSize || 1)));
    } catch (error) {
      console.error("Error fetching models:", error);
      toast.error(getApiErrorMessage(error, t("aiCatalog.failedToLoadModels")));
    } finally {
      setLoading(false);
    }
  }, [
    safeSelectedProjects,
    debouncedValue,
    provider,
    source,
    type,
    page,
    pageSize,
    t,
  ]);

  const handleOpenForm = (id: string | null, nextIsPublic: boolean) => {
    setSelectedModelId(id);
    setAnchorEl(null);
    setIsPublic(nextIsPublic);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setSelectedModelId(null);
  };

  const handleOpenShareModal = (
    event: React.MouseEvent<HTMLElement>,
    model: LLMModel,
  ) => {
    event.stopPropagation();
    setSelectedModel(model);
    setShareOpen(true);
  };

  const handleCloseShareModal = () => {
    setShareOpen(false);
    setSelectedModel(null);
  };

  const handleOpenDeleteModal = (model: LLMModel) => {
    setDeleteOpen(true);
    setSelectedModel(model);
  };

  const handleCloseDeleteModal = () => {
    setDeleteOpen(false);
    setSelectedModel(null);
  };

  const handleSortName = () => {
    setOrder((previous) => (previous === "asc" ? "desc" : "asc"));
  };

  const handleOpenDetail = (model: LLMModel) => {
    setSelectedModelId(model.id);
    setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedModelId(null);
  };

  const resetToFirstPage = () => {
    setPage((currentPage) => (currentPage === 1 ? currentPage : 1));
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleProviderChange = (value: string) => {
    setProvider(value);
    resetToFirstPage();
  };

  const handleSourceChange = (value: ModelSourceFilter) => {
    setSource(value);
    resetToFirstPage();
  };

  const handleTypeChange = (value: ModelFilterType) => {
    setType(value);
    resetToFirstPage();
  };

  const handlePageSizeChange = (value: number) => {
    setPageSize(value);
    resetToFirstPage();
  };

  const sortedModels = useMemo(
    () => sortModelsByName(models, order),
    [models, order],
  );

  useEffect(() => {
    fetchModels();
    setIsAdmin(isUserAdmin());

    if (authProvider === "keycloak" && isKeycloakAuthenticated()) {
      const keycloakUser = getKeycloakUserInfo();
      if (keycloakUser?.email) setCurrentUserEmail(keycloakUser.email);
    } else if (currentUser?.email) {
      setCurrentUserEmail(currentUser.email);
    } else if (userProfile?.email) {
      setCurrentUserEmail(userProfile.email);
    }
  }, [currentUser, userProfile, authProvider, fetchModels]);

  useEffect(() => {
    window.addEventListener(PROJECT_SELECTION_CHANGED_EVENT, fetchModels);
    return () => {
      window.removeEventListener(PROJECT_SELECTION_CHANGED_EVENT, fetchModels);
    };
  }, [fetchModels]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedValue(searchTerm);
      resetToFirstPage();
    }, 500);

    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  return (
    <>
      <Box className="px-6 py-8">
        <CatalogToolbar
          t={t}
          loading={loading}
          isAdmin={isAdmin}
          searchTerm={searchTerm}
          provider={provider}
          source={source}
          type={type}
          providers={providers}
          sources={sources}
          types={types}
          anchorEl={anchorEl}
          openMenu={openMenu}
          onSearchChange={handleSearchChange}
          onProviderChange={handleProviderChange}
          onSourceChange={handleSourceChange}
          onTypeChange={handleTypeChange}
          onOpenMenu={(event) => setAnchorEl(event.currentTarget)}
          onCloseMenu={() => setAnchorEl(null)}
          onOpenModal={handleOpenForm}
        />

        <CatalogTable
          t={t}
          isRTL={isRTL}
          loading={loading}
          models={sortedModels}
          currentUserId={cache?.id}
          order={order}
          onSortName={handleSortName}
          onShare={handleOpenShareModal}
          onCopyEndpoint={copyToClipboard}
          onOpenDetail={handleOpenDetail}
        />

        <CatalogPagination
          t={t}
          loading={loading}
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
        />
      </Box>

      <AICatalogDetailComp
        selectedModelId={selectedModelId || undefined}
        open={detailOpen}
        onClose={handleCloseDetail}
        isAdmin={isAdmin}
        handleOpenModal={handleOpenForm}
        handleOpenDeleteModal={handleOpenDeleteModal}
      />

      <AICatalogFormComp
        selectedModelId={selectedModelId}
        isPublic={isPublic}
        open={formOpen}
        onClose={handleCloseForm}
        onSuccess={fetchModels}
      />

      <AICatalogShareComp
        open={shareOpen}
        onClose={handleCloseShareModal}
        model={selectedModel}
        onShared={fetchModels}
      />

      <AICatalogConfirmDeleteComp
        selectedModel={selectedModel}
        open={deleteOpen}
        onClose={handleCloseDeleteModal}
        onSuccess={() => {
          handleCloseDetail();
          fetchModels();
        }}
      />
    </>
  );
};

export default AICatalogComp;
